import React, { useContext, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import Avatar from '../common/Avatar';
import AppContext from '../../context/Context';
import { API_BASE_CANDIDATES } from '../../apiConfig';

const createInitialForm = user => ({
  fullName: user?.full_name || '',
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const ProfileDropdown = () => {
  const { user, clearAuth, setUser, setToken } = useContext(AppContext);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formState, setFormState] = useState(createInitialForm(user));

  const endpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/p-admin/auth.php?action=profile`),
    []
  );

  const resetModalState = () => {
    setErrorMessage('');
    setFormState(createInitialForm(user));
  };

  const toggleProfileModal = () => {
    if (isSaving) {
      return;
    }

    const nextOpen = !isModalOpen;
    setIsModalOpen(nextOpen);

    if (nextOpen) {
      setErrorMessage('');
      setFormState(createInitialForm(user));
      return;
    }

    resetModalState();
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;

    setFormState(current => ({
      ...current,
      [name]: value
    }));
  };

  const submitProfileUpdate = async payload => {
    let lastError = null;

    for (const endpoint of endpointCandidates) {
      try {
        return await axios.post(endpoint, payload, {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        lastError = error;
        const status = error.response?.status || 0;

        if (status && ![404, 502, 503, 504].includes(status)) {
          throw error;
        }
      }
    }

    throw lastError;
  };

  const handleProfileSave = async event => {
    event.preventDefault();

    const fullName = formState.fullName.trim();
    const currentPassword = formState.currentPassword;
    const newPassword = formState.newPassword;
    const confirmPassword = formState.confirmPassword;
    const wantsPasswordChange = currentPassword || newPassword || confirmPassword;

    if (fullName.length < 3) {
      setErrorMessage("F.I.Sh kamida 3 ta belgidan iborat bo'lishi kerak.");
      return;
    }

    if (wantsPasswordChange) {
      if (!currentPassword) {
        setErrorMessage("Parolni almashtirish uchun joriy parolni kiriting.");
        return;
      }

      if (newPassword.length < 8) {
        setErrorMessage("Yangi parol kamida 8 ta belgidan iborat bo'lishi kerak.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setErrorMessage("Yangi parol va tasdiq paroli mos emas.");
        return;
      }
    }

    setIsSaving(true);
    setErrorMessage('');

    try {
      const response = await submitProfileUpdate({
        full_name: fullName,
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      if (response.data?.success) {
        if (response.data.token) {
          setToken(response.data.token);
        }

        if (response.data.user) {
          setUser(response.data.user);
        }

        toast.success(response.data.message || 'Profil muvaffaqiyatli yangilandi.');
        setIsModalOpen(false);
        resetModalState();
        return;
      }

      setErrorMessage(response.data?.message || 'Profilni saqlashda xatolik yuz berdi.');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : '') ||
        'Profilni saqlashda xatolik yuz berdi.';

      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);

    try {
      await axios.post(`${API_BASE_CANDIDATES[0]}/p-admin/auth.php?action=logout`);
    } catch (error) {
      // Server-side logout failed, but local token is still cleared below.
    } finally {
      clearAuth();

      if (typeof window !== 'undefined') {
        window.location.assign('/authentication/basic/login?panel=admin');
      }

      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <UncontrolledDropdown nav inNavbar>
        <DropdownToggle nav className="pr-0">
          <Avatar name={user?.full_name || 'P'} size="xl" />
        </DropdownToggle>
        <DropdownMenu right className="dropdown-menu-card">
          <div className="bg-white rounded-soft py-2">
            <div className="px-3 pb-2 border-bottom">
              <div className="font-weight-bold text-dark">{user?.full_name || 'Foydalanuvchi'}</div>
              <div className="fs--2 text-500 text-uppercase">{user?.role_label || 'P-admin'}</div>
            </div>
            <DropdownItem disabled>{user?.job_title || 'Psixologik modul foydalanuvchisi'}</DropdownItem>
            <DropdownItem onClick={toggleProfileModal}>
              <FontAwesomeIcon icon="edit" className="mr-2" />
              Profilni tahrirlash
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={handleLogout}>
              <FontAwesomeIcon icon="sign-out-alt" className="mr-2" />
              {isLoggingOut ? 'Yopilmoqda...' : 'Chiqish'}
            </DropdownItem>
          </div>
        </DropdownMenu>
      </UncontrolledDropdown>

      <Modal isOpen={isModalOpen} toggle={toggleProfileModal} centered className="glass-modal">
        <ModalHeader toggle={toggleProfileModal}>Profilni tahrirlash</ModalHeader>
        <Form onSubmit={handleProfileSave}>
          <ModalBody>
            {errorMessage ? (
              <Alert color="danger" className="mb-3">
                {errorMessage}
              </Alert>
            ) : null}

            <FormGroup>
              <Label for="profile-full-name">F.I.Sh</Label>
              <Input
                id="profile-full-name"
                name="fullName"
                value={formState.fullName}
                onChange={handleChange}
                autoComplete="name"
                disabled={isSaving}
              />
            </FormGroup>

            <div className="small text-500 mb-3">
              Parolni almashtirish majburiy emas. O'zgartirmoqchi bo'lsangiz, quyidagi maydonlarni to'ldiring.
            </div>

            <FormGroup>
              <Label for="profile-current-password">Joriy parol</Label>
              <Input
                id="profile-current-password"
                name="currentPassword"
                type="password"
                value={formState.currentPassword}
                onChange={handleChange}
                autoComplete="current-password"
                disabled={isSaving}
              />
            </FormGroup>

            <FormGroup>
              <Label for="profile-new-password">Yangi parol</Label>
              <Input
                id="profile-new-password"
                name="newPassword"
                type="password"
                value={formState.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isSaving}
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Label for="profile-confirm-password">Yangi parolni tasdiqlang</Label>
              <Input
                id="profile-confirm-password"
                name="confirmPassword"
                type="password"
                value={formState.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isSaving}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleProfileModal} disabled={isSaving}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Saqlanmoqda...' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default ProfileDropdown;
